import useAirPlusLmsLanguage from 'src/hooks/useAirPlusLmsLanguage';
import React from 'react';
import styled from 'styled-components';
import { Drawer, Tooltip } from 'antd';
import { useReactiveVar } from '@apollo/client';
import { userInfoVar } from 'src/apollo/cache';
import LibraryNavigation from './LibraryNavigation';
import { useHistory, useLocation } from 'react-router-dom';
import {
  HomeOutlined,
  ReadOutlined,
  BookOutlined,
  BarChartOutlined,
  UserOutlined,
  FireFilled,
  FileTextOutlined,
  FormOutlined,
} from '@ant-design/icons';

const SidebarWrapper = styled.aside`
  width: ${({ $collapsed }) => ($collapsed ? '76px' : '228px')};
  min-width: ${({ $collapsed }) => ($collapsed ? '76px' : '228px')};
  height: 100%;
  background: #ffffff;
  border-right: 1.5px solid rgba(8, 145, 178, 0.12);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: ${({ $collapsed }) => ($collapsed ? '16px 8px' : '16px 12px')};
  overflow-y: auto;
  position: sticky;
  top: 0;
  z-index: 20;
  transition: all 0.22s ease;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(8, 145, 178, 0.2);
    border-radius: 4px;
  }

  ${({ $mobile }) => $mobile && `
    width: 100%; min-width: 0; position: static; border-right: 0;
    height: auto; min-height: 100%; overflow: visible;
    padding: 0 12px 16px;
  `}

`;

const NavSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

`;

const SectionHeader = styled.div`
  font-family: 'Nunito', Arial, 'Pretendard Variable', sans-serif;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #8fa3bb;
  padding: 12px 10px 6px;
  display: ${({ $collapsed }) => ($collapsed ? 'none' : 'block')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  justify-content: ${({ $collapsed }) => ($collapsed ? 'center' : 'flex-start')};
  gap: 12px;
  width: 100%;
  padding: ${({ $collapsed }) => ($collapsed ? '10px 0' : '10px 14px')};
  border-radius: 12px;
  border: 1.5px solid ${({ $active }) => ($active ? 'rgba(8, 145, 178, 0.25)' : 'transparent')};
  background: ${({ $active }) =>
    $active ? 'linear-gradient(135deg, rgba(8, 145, 178, 0.12), rgba(34, 211, 238, 0.08))' : 'transparent'};
  color: ${({ $active }) => ($active ? '#0891b2' : '#4a6380')};
  font-family: 'Nunito', Arial, 'Pretendard Variable', sans-serif;
  font-size: 14.5px;
  font-weight: ${({ $active }) => ($active ? '800' : '700')};
  cursor: pointer;
  transition: background-color 0.18s ease, color 0.18s ease, border-color 0.18s ease;
  line-height: 1.4;
  text-align: left;
  position: relative;

  .item-left {
    display: flex;
    align-items: center;
    justify-content: ${({ $collapsed }) => ($collapsed ? 'center' : 'flex-start')};
    width: ${({ $collapsed }) => ($collapsed ? '100%' : 'auto')};
    gap: 12px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .anticon {
    font-size: 18px;
    color: ${({ $active }) => ($active ? '#0891b2' : '#8fa3bb')};
    transition: color 0.18s ease;
    flex-shrink: 0;
  }

  .label {
    display: ${({ $collapsed }) => $collapsed ? 'none' : 'inline'};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .mobile-label { display: none; }

  &:hover {
    background: rgba(8, 145, 178, 0.08);
    color: #0891b2;
    .anticon {
      color: #0891b2;
    }
  }

`;

const StreakCard = styled.div`
  background: linear-gradient(135deg, #fff7ed, #ffedd5);
  border: 1.5px solid rgba(249, 115, 22, 0.25);
  border-radius: 16px;
  padding: ${({ $collapsed }) => ($collapsed ? '10px 6px' : '14px')};
  display: flex;
  align-items: center;
  justify-content: ${({ $collapsed }) => ($collapsed ? 'center' : 'flex-start')};
  gap: 12px;
  margin-top: 16px;
  transition: all 0.2s ease;

  .fire-icon {
    width: 36px;
    height: 36px;
    border-radius: 12px;
    background: #f97316;
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    box-shadow: 0 4px 12px rgba(249, 115, 22, 0.3);
    flex-shrink: 0;
  }

  .info {
    display: flex;
    flex-direction: column;
    .title {
      font-size: 11px;
      font-weight: 700;
      color: #c2410c;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
    .count {
      font-size: 16px;
      font-weight: 900;
      color: #7c2d12;
      line-height: 1.1;
    }
  }
`;

function AirPlusLeftMenu({ collapsed = false, streakDays = 0, mobile = false, onClose }) {
  const { t: airT, text } = useAirPlusLmsLanguage();
  const history = useHistory();
  const location = useLocation();
  const menuCollapsed = mobile ? false : collapsed;
  const user = useReactiveVar(userInfoVar);
  const currentPath = location.pathname;
  // My Learning section
  const isTeacherOrCampus = [1, 2, 6].includes(Number(user?.type));
  const query = new URLSearchParams(location.search);
  const isShelf = query.get('view') === 'shelf';
  const shelfParams = new URLSearchParams({ view: 'shelf', shelf: isTeacherOrCampus ? 'favorites' : 'reading' });
  if (['alist', 'levelup'].includes(query.get('service'))) shelfParams.set('service', query.get('service'));
  const learningItems = [
    { label: airT("Dashboard"), mobileLabel: text('Home','홈'), path: '/air-plus', icon: <HomeOutlined />, exact: true },
    { label: airT("Library"), mobileLabel: text('Library','도서관'), path: '/air-plus/library', icon: <ReadOutlined /> },
    { label: text('My Bookshelf','내 서재'), mobileLabel: text('Shelf','서재'), path: `/air-plus/library?${shelfParams}`, icon: <BookOutlined />, shelf: true },
    { label: airT("Portfolio"), mobileLabel: text('Records','기록'), path: '/air-plus/portfolio', icon: <BarChartOutlined /> },
    { label: airT("Book Report"), mobileLabel: text('Report','리포트'), path: '/air-plus/book-reports', icon: <FileTextOutlined /> },
    { label: airT("My Page"), mobileLabel: text('Me','마이'), path: '/air-plus/mypage', icon: <UserOutlined /> },
    { label: airT("Level Test"), mobileLabel: text('Test','테스트'), path: '/air-plus/level-test', icon: <FormOutlined /> },
  ];

  const isActive = (item) => {
    if (item.shelf) return currentPath.replace(/\/$/, '') === '/air-plus/library' && isShelf;
    if (item.path === '/air-plus/library') return currentPath.startsWith(item.path) && !isShelf;
    if (item.exact) return currentPath === item.path;
    return currentPath.startsWith(item.path);
  };
  const openLibrary = () => {
    if (!mobile) window.dispatchEvent(new CustomEvent('airPlusSidebarStateChange', { detail: false }));
    history.push({ pathname: '/air-plus/library', search: '?service=alist', state: { resetLibraryFilters: true, focusLibrary: true } });
  };

  const renderItem = (item) => {
    const active = isActive(item);
    const content = (
      <MenuItem
        key={item.path}
        $active={active}
        $collapsed={menuCollapsed}
        aria-label={item.label}
        aria-current={active ? 'page' : undefined}
        onClick={() => {
          if (item.path === '/air-plus/library') openLibrary();
          else history.push(item.path);
          if (mobile) onClose?.();
        }}
      >
        <div className="item-left">
          {item.icon}
          <span className="label">{item.label}</span>
          <span className="mobile-label">{item.mobileLabel}</span>
        </div>
      </MenuItem>
    );

    if (collapsed && !mobile) {
      return (
        <Tooltip key={item.path} placement="right" title={airT(item.label)}>
          {content}
        </Tooltip>
      );
    }
    return content;
  };

  const navigation = (
    <SidebarWrapper as="nav" aria-label={text('Learning navigation','학습 메뉴')} $collapsed={menuCollapsed} $mobile={mobile}>
      <div>
        {/* SECTION 1: My Learning */}
        <NavSection>
          {!mobile && <SectionHeader $collapsed={menuCollapsed}>{airT("My Learning")}</SectionHeader>}
          {learningItems.map(renderItem)}
        </NavSection>
        <LibraryNavigation collapsed={menuCollapsed} mobile={mobile} isStudent={Number(user?.type) === 0} onLevelSelect={mobile ? onClose : undefined} />
      </div>

      <Tooltip
        placement={collapsed ? 'right' : 'top'}
        title={`${streakDays} ${streakDays === 1 ? airT("Day") : airT("Days")} Continuous Study!`}
      >
        <StreakCard className="desktop-only" $collapsed={menuCollapsed}>
          <div className="fire-icon">
            <FireFilled />
          </div>
          {!menuCollapsed && (
            <div className="info">
              <span className="title">{airT("Continuous Study")}</span>
              <span className="count">
                {streakDays} {streakDays === 1 ? airT("Day") : airT("Days")}{airT(" Streak! ")}</span>
            </div>
          )}
        </StreakCard>
      </Tooltip>
    </SidebarWrapper>
  );

  if (mobile) {
    return (
      <Drawer
        title={airT('My Learning')}
        placement="left"
        width="min(280px, 85vw)"
        open={!collapsed}
        onClose={onClose}
        bodyStyle={{ padding: 0 }}
      >
        {navigation}
      </Drawer>
    );
  }
  return navigation;
}

export default AirPlusLeftMenu;
